import * as React from 'react';
import hoistNonReactMethods from 'hoist-non-react-methods';
import { getDisplayName } from './utils/getDisplayName';
import { st, classes } from './Focusable.st.css';
import { isStatelessComponent } from './utils/isStatelessComponent';
import { shellClasses } from '../../../../../common/shellClasses';
import { inputMethod } from './utils/inputMethod';
import classNames from 'classnames';
/**
 * Assigned the given propTypes to the given class.
 *
 * This is a hack because since Yoshi3, with babel-preset-yoshi,
 * the babel-plugin-transform-react-remove-prop-types is enabled and removes propTypes.
 *
 * So if we simply do FocusableHOC.propTypes = Component.propTypes, it is being stripped away.
 *
 * This later becomes a problem if another component defines:
 * <code>
 * Comp.propTypes = {
 *   prop1: SomeFocusableComp.propTypes.prop1
 * }
 * </code>
 */
const assignPropTypesHack = (target, propTypes) => {
    target.propTypes = propTypes;
};
export function withFocusable(Component, options = { hasCustomFocus: false }) {
    class Focusable extends React.Component {
        constructor() {
            super(...arguments);
            this.wrappedComponentRef = null;
            this.state = {
                focus: false,
                focusVisible: false,
            };
            this.focus = () => {
                if (this.wrappedComponentRef?.focus) {
                    this.wrappedComponentRef.focus();
                }
            };
            this.markAsFocused = () => {
                this.setState({
                    focus: true,
                    focusVisible: options.isFocusWithMouse || inputMethod.isKeyboard(),
                });
                inputMethod.subscribe(this, () => {
                    if (options.isFocusWithMouse || inputMethod.isKeyboard()) {
                        this.setState({ focusVisible: true });
                    }
                });
            };
            this.markAsBlurred = () => {
                inputMethod.unsubscribe(this);
                this.setState({ focus: false, focusVisible: false });
            };
            this.onFocus = (event) => {
                const { onFocus } = this.props;
                onFocus ? onFocus(event, this.getTriggers()) : this.markAsFocused();
            };
            this.onBlur = (event) => {
                const { onBlur } = this.props;
                onBlur ? onBlur(event, this.getTriggers()) : this.markAsBlurred();
            };
        }
        componentWillUnmount() {
            inputMethod.unsubscribe(this);
        }
        componentDidUpdate(prevProps) {
            /*
              in case when button was focused and then become disabled,
              we need to trigger blur logic and remove all listers, as disabled button
              do not trigger onFocus and onBlur events
            */
            const isFocused = this.state.focus || this.state.focusVisible;
            const isBecomeDisabled = !prevProps.disabled && this.props.disabled;
            if (isFocused && isBecomeDisabled) {
                this.onBlur({});
            }
        }
        getTriggers() {
            return { blur: this.markAsBlurred, focus: this.markAsFocused };
        }
        render() {
            const reference = isStatelessComponent(Component)
                ? undefined
                : (ref) => {
                    this.wrappedComponentRef = ref;
                };
            const { className, onBlur, onFocus, ...rest } = this.props;
            return (React.createElement(Component, { ...rest, ref: reference, className: st(classes.root, {
                    focus: this.state.focus,
                    'focus-visible': this.state.focusVisible,
                }, className, classNames({
                    [shellClasses.hasCustomFocus]: options.hasCustomFocus,
                    [shellClasses.ignoreFocus]: options.ignoreFocus,
                })), onBlur: this.onBlur, onFocus: this.onFocus, 'data-focusable-focus': this.state.focus,
                'data-focusable-focus-visible': this.state.focusVisible }));
        }
    }
    Focusable.displayName = getDisplayName(Component);
    Focusable.defaultProps = Component.defaultProps;
    // eslint-disable-next-line react/forbid-foreign-prop-types
    assignPropTypesHack(Focusable, Component.propTypes);
    return isStatelessComponent(Component)
        ? Focusable
        : hoistNonReactMethods(Focusable, Component, {
            delegateTo: (c) => c.wrappedComponentRef,
            hoistStatics: true,
        });
}
//# sourceMappingURL=Focusable.js.map